import {useState} from 'react';
import clsx from 'clsx';
import {Link} from 'react-router-dom';
import {Button} from '@common/ui/buttons/button';
import {IconButton} from '@common/ui/buttons/icon-button';
import {CloseIcon} from '@common/icons/material/Close';
import {Trans} from '@common/i18n/trans';
import {useTrans} from '@common/i18n/use-trans';
import {useTrialDaysLeft} from '@common/billing/trials/use-trial-days-left';
import {arabicDayCount} from '@common/billing/trials/arabic-day-count';

// per browser tab: the banner comes back on the next visit so the countdown
// is not forgotten for the rest of the trial
export const TRIAL_BANNER_DISMISSED_KEY = 'be.trial.banner.dismissed';

function isDismissed(): boolean {
  try {
    return sessionStorage.getItem(TRIAL_BANNER_DISMISSED_KEY) === '1';
  } catch (e) {
    return false;
  }
}

function rememberDismissed() {
  try {
    sessionStorage.setItem(TRIAL_BANNER_DISMISSED_KEY, '1');
  } catch (e) {
    // storage blocked (private mode etc.) - hide for this render only
  }
}

interface TrialBannerProps {
  className?: string;
}

/**
 * Slim countdown shown to users on a running free trial, with a way to
 * subscribe. Renders nothing for everyone else.
 */
export function TrialBanner({className}: TrialBannerProps) {
  const trial = useTrialDaysLeft();
  const {trans} = useTrans();
  const [dismissed, setDismissed] = useState(isDismissed);

  if (!trial || dismissed) return null;

  return (
    <div
      role="status"
      // the text is Arabic also on the English site
      dir="auto"
      className={clsx(
        'flex items-center gap-12 border-b border-primary/20 bg-primary/5 px-16 py-8 md:px-30',
        className,
      )}
    >
      <div className="min-w-0 flex-auto">
        <div className="text-sm font-bold text-primary">
          <TrialCountdown daysLeft={trial.daysLeft} />
        </div>
        <div className="text-xs text-muted">
          <Trans message="اشترك الآن ويستمر استماعك بدون انقطاع" />
        </div>
      </div>
      <Button
        variant="outline"
        size="xs"
        color="primary"
        className="flex-shrink-0"
        elementType={Link}
        to="/pricing"
      >
        <Trans message="اشترك الآن" />
      </Button>
      <IconButton
        size="sm"
        className="flex-shrink-0 text-muted"
        aria-label={trans({message: 'إخفاء'})}
        onClick={() => {
          rememberDismissed();
          setDismissed(true);
        }}
      >
        <CloseIcon />
      </IconButton>
    </div>
  );
}

function TrialCountdown({daysLeft}: {daysLeft: number}) {
  if (daysLeft <= 0) {
    return <Trans message="تجربتك المجانية تنتهي اليوم" />;
  }
  if (daysLeft === 1) {
    return <Trans message="تجربتك المجانية تنتهي غداً" />;
  }
  return (
    <Trans
      message="تجربتك المجانية: باقي :days"
      values={{days: arabicDayCount(daysLeft)}}
    />
  );
}

/**
 * Notice at the top of the pricing page for users on a running free trial.
 * Subscribing during a trial ends the free days immediately and starts the
 * paid subscription, so it says exactly that. Renders nothing for everyone
 * else.
 */
export function TrialPricingNotice({className}: TrialBannerProps) {
  const trial = useTrialDaysLeft();
  if (!trial) return null;

  return (
    <div
      role="status"
      // the text is Arabic also on the English site
      dir="auto"
      className={clsx(
        'mx-auto mt-24 max-w-[620px] rounded-2xl border border-primary/30 bg-primary/5 px-18 py-14 text-center',
        className,
      )}
    >
      <div className="text-sm font-bold text-primary md:text-base">
        <TrialPricingCountdown daysLeft={trial.daysLeft} />
      </div>
      <div className="mt-4 text-xs leading-relaxed text-muted md:text-sm">
        <Trans message="عند الاشتراك الآن تنتهي التجربة المجانية ويبدأ اشتراكك المدفوع فوراً." />
      </div>
    </div>
  );
}

function TrialPricingCountdown({daysLeft}: {daysLeft: number}) {
  if (daysLeft <= 0) {
    return <Trans message="أنت في التجربة المجانية: تنتهي اليوم" />;
  }
  if (daysLeft === 1) {
    return <Trans message="أنت في التجربة المجانية: تنتهي غداً" />;
  }
  return (
    <Trans
      message="أنت في التجربة المجانية: باقي :days"
      values={{days: arabicDayCount(daysLeft)}}
    />
  );
}
